import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Card, CardBody, Spinner } from "@material-tailwind/react";
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TaskIcon from '@mui/icons-material/Task';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import {useTramites} from '../hooks/useTramites';
import {transformarIso8601} from '../../../Helpers/fechas'

const MAPEO_ORDEN = {
    "Trámite": "tipo",
    "Descripción": "descripcion",
    "Reg.": "region",
    "Estado": "status_actual",
    "Fecha": "fecha"
};

export const TablaTramites = ({ data, seleccionarTramite, loading, filtro = 'estados',noHeader,mensajeError }) => {
    const [orden, setOrden] = useState('fecha');
    const [rotar, setRotar] = useState(2);
    const [valorFiltroActivo, setValorFiltroActivo] = useState(null);
    const { ESTADOS_CONFIG,TIPOS_TRAMITE } = useTramites();
    const [stringSubBusqueda, setStringSubBusqueda] = useState('');

    // --- FUNCIONES AUXILIARES ---
    
    const mapearTipo = (tipoKey) => {
        const encontrado = TIPOS_TRAMITE.find(t => t.tipo === tipoKey);
        return encontrado ? encontrado.label : 'Desconocido';
    };

    const mostrarEstado = (status_actual) => {
        const iconStyle = { fontSize: 18 };
        switch (status_actual) {
            case 'Iniciado': return <div className='flex items-center gap-1'><HowToVoteIcon sx={{ color: '#bd9651', ...iconStyle }} /> <span className='text-[11px] font-bold'>Iniciado</span></div>;
            case 'En proceso regional': return <div className='flex items-center gap-1'><FactCheckIcon sx={{ color: '#6b97dd', ...iconStyle }} /><span className='text-[11px] font-bold'>En región</span></div>;
            case 'En proceso sede': return <div className='flex items-center gap-1'><TaskIcon sx={{ color: '#45c7aa', ...iconStyle }} /><span className='text-[11px] font-bold'>En sede</span></div>;
            case 'Aprobado': return <div className='flex items-center gap-1'><ThumbUpOffAltIcon sx={{ color: '#4cd34c', ...iconStyle }} /><span className='text-[11px] font-bold text-green-700'>Aprobado</span></div>;
            case 'Rechazado': return <div className='flex items-center gap-1'><ThumbDownOffAltIcon sx={{ color: '#ff5656', ...iconStyle }} /><span className='text-[11px] font-bold text-red-700'>Rechazado</span></div>;
            default: return <span className='text-[11px]'>{status_actual}</span>;
        }
    };

    const sortFunction = (a, b) => {
            // Obtenemos el campo real según el encabezado clickeado
            const campo = MAPEO_ORDEN[orden] || 'fecha';
            
            let valA, valB;

            // Lógica especial para campos anidados o complejos
            if (orden === 'Descripción') {
                valA = a.ministro?.nombre?.toLowerCase() || '';
                valB = b.ministro?.nombre?.toLowerCase() || '';
            } else if (orden === 'Estado') {
                // Obtenemos el texto del último estado del array
                valA = a.status[a.status.length - 1]?.texto || '';
                valB = b.status[b.status.length - 1]?.texto || '';
            } else {
                valA = a[campo];
                valB = b[campo];
            }

            // Manejo de nulos y strings para comparación
            if (typeof valA === 'string') valA = valA.toLowerCase();
            if (typeof valB === 'string') valB = valB.toLowerCase();

            if (valA === valB) return 0;
            
            // Aplicamos la rotación (ascendente/descendente)
            const comparacion = valA > valB ? 1 : -1;
            return rotar === 1 ? comparacion : -comparacion;
        }
    // --- LÓGICA DE CONTEO Y FILTRADO ---

    const { itemsParaCards, datosFiltrados } = useMemo(() => {

        if (!data) return { itemsParaCards: [], datosFiltrados: [] };
        if(noHeader) return {itemsParaCards: [], datosFiltrados: data.sort(sortFunction)}
        // 1. Decidir qué cards mostrar según la prop 'filtro'
        // Si filtro === 'tipo', mostramos cards de ESTADOS. Si filtro === 'estados', mostramos cards de TIPO.
        const configuracionCards = filtro === 'tipo' ? ESTADOS_CONFIG : TIPOS_TRAMITE;
        
        // 2. Contar elementos para las cards
        const conteos = {};
        configuracionCards.forEach(c => conteos[c.value] = 0);

        data.forEach(item => {
            const val = filtro === 'tipo' 
                ? item.status_actual 
                : item.tipo;
            if (conteos.hasOwnProperty(val)) conteos[val]++;
        });

        const cardsConData = configuracionCards.map(c => ({
            ...c,
            cantidad: conteos[c.value] || 0
        }));

        // 3. Filtrar la tabla
        let filtrados = [...data];
        if (valorFiltroActivo) {
            filtrados = filtrados.filter(item => {
                const valItem = filtro === 'estados' 
                    ? item.tipo  
                    : item.status_actual;
                return valItem === valorFiltroActivo;
            });
        }

        // 4. Ordenar
        filtrados.sort(sortFunction);

        return { itemsParaCards: cardsConData, datosFiltrados: filtrados };
    }, [data, filtro, valorFiltroActivo, orden, rotar]);

    useEffect(()=>{
        setValorFiltroActivo(null)
    },[data])

    const callbackFilter = (item) => {
    if (!stringSubBusqueda) return true;

    // Función interna para limpiar acentos
    const normalizar = (texto) => {
        return texto
            ?.toString()
            ?.normalize("NFD") // Descompone caracteres (á -> a + ´)
            ?.replace(/[\u0300-\u036f]/g, "") // Elimina los acentos
            ?.toLowerCase() ?? "";
    };

    const query = normalizar(stringSubBusqueda);
    
    // Normalizamos cada campo antes de comparar
    const descripcion = normalizar(item?.descripcion);
    const nombreMinistro = normalizar(item?.ministro?.nombre);
    const codigoWeb = normalizar(item?.codigo_web);

    return (
        descripcion.includes(query) || 
        nombreMinistro.includes(query) || 
        codigoWeb.includes(query)
    );
};

    if (loading) return (
        <div className='flex flex-col justify-center items-center h-64'>
            <Spinner className="h-10 w-10" />
            <Typography className="mt-4 text-gray-500">Buscando trámites...</Typography>
        </div>
    );

    if(mensajeError) return (
        <div className='flex flex-col justify-center items-center h-64 p-4'>
            <Typography className="mt-4 text-gray-500">{mensajeError}</Typography>
        </div>
    );


    return (
        <div className="w-full space-y-5 p-2 animate-fade-in">
            {/* GRUPO DE CARDS UNIFICADO (Dinámico según prop 'filtro') */}
            {!noHeader && <div className="space-y-3">
                <Typography className="font-black text-gray-400 px-1 tracking-tighter uppercase text-[10px]">
                    {filtro === 'tipo' ? 'Filtrar por Etapa del Proceso' : 'Filtrar por Categoría de Trámite'}
                </Typography>
                
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
                    {itemsParaCards.map((item) => (
                        <div
                            key={item.key || item.tipo}
                            onClick={() => setValorFiltroActivo(valorFiltroActivo === item.value ? null : item.value)}
                            className={`relative overflow-hidden bg-white border-2 transition-all cursor-pointer rounded-xl p-3 shadow-sm
                                ${valorFiltroActivo === item.value 
                                    ? 'border-blue-600 bg-blue-50/30 ring-4 ring-blue-50' 
                                    : 'border-gray-100 hover:border-gray-300 hover:shadow-md'}`}
                        >
                            <Typography className="text-gray-500 font-bold text-[9px] uppercase leading-tight mb-1">
                                {item.label}
                            </Typography>
                            <div className="flex items-center justify-between">
                                <Typography variant="h4" className={`font-black ${valorFiltroActivo === item.value ? 'text-blue-700' : 'text-gray-800'}`}>
                                    {item.cantidad}
                                </Typography>
                                {valorFiltroActivo === item.value && (
                                    <div className="h-1.5 w-1.5 rounded-full bg-blue-600 animate-pulse"></div>
                                )}
                            </div>
                            {/* Indicador visual inferior estilo dashboard */}
                            <div className={`absolute bottom-0 left-0 h-1 w-full ${valorFiltroActivo === item.value ? 'bg-blue-600' : 'bg-transparent'}`}></div>
                        </div>
                    ))}
                </div>
            </div>}

            <div className="relative w-full md:w-96 ml-auto">
                <input
                    type="text"
                    placeholder="Buscar por descripción, ministro o código..."
                    value={stringSubBusqueda}
                    onChange={(e) => setStringSubBusqueda(e.target.value)}
                    className="w-full h-10 pl-3 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
            </div>

            {/* TABLA DE RESULTADOS */}
            <Card className="shadow-sm border border-gray-200 overflow-hidden rounded-xl">
                <CardBody className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full table-auto text-left">
                            <thead>
                                <tr className="bg-gray-50/50 border-b border-gray-200">
                                    {["Trámite", "Descripción", "Reg.", "Estado", "Fecha"].map((head) => (
                                        <th 
                                            key={head} 
                                            className={`p-4 cursor-pointer hover:bg-gray-100 transition-colors ${orden === head ? 'bg-blue-50/50' : ''}`}
                                            onClick={() => {
                                                if (orden === head) {
                                                    setRotar(prev => prev === 1 ? 2 : 1);
                                                } else {
                                                    setOrden(head);
                                                    setRotar(1); 
                                                }
                                            }}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Typography className={`font-bold uppercase text-[10px] tracking-widest ${orden === head ? 'text-blue-700' : 'text-gray-600'}`}>
                                                    {head}
                                                </Typography>
                                                <ChevronUpDownIcon className={`h-3 w-3 transition-transform ${orden === head ? (rotar === 1 ? 'text-blue-700 rotate-180' : 'text-blue-700') : 'text-gray-400'}`} />
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {datosFiltrados.filter(callbackFilter).map((item, index) => (
                                    <tr
                                        key={item._id || index}
                                        onClick={() => seleccionarTramite(item)}
                                        className="hover:bg-blue-50/20 cursor-pointer transition-colors"
                                    >
                                        <td className="p-4">
                                            <span className="text-[12px] font-bold text-blue-700 bg-blue-50 px-2 py-1 rounded">
                                                {mapearTipo(item.tipo)}
                                            </span>
                                            <br />
                                            <span className="font-mono text-[11px] text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-100">
                                                {item.codigo_web}
                                            </span>
                                        </td>
                                        <td className="p-4 text-[13px] font-medium text-gray-800">
                                            {item.ministro.nombre}
                                            <br />
                                            {item.descripcion}
                                        </td>
                                        <td className="p-4 text-[12px] text-gray-500 font-mono">{item.region}</td>
                                        <td className="p-4">{mostrarEstado(item.status[item.status.length - 1].texto)}</td>
                                        <td className="p-4 text-[12px] text-gray-500 font-mono">{transformarIso8601(item.fecha)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {datosFiltrados.length === 0 && (
                        <div className="p-12 text-center">
                            <Typography className="text-gray-400 text-sm">No hay registros para este filtro.</Typography>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};